import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { RouterLink } from '@angular/router';
import {forkJoin, Observable, of} from 'rxjs';
import {mergeMap} from 'rxjs/operators';
import { GetEmpty } from 'src/app/classes/Empties';
import { NumeroALetras } from 'src/app/classes/NumeroALetras';
import {ItemOptionInfo, OrderInfo, OrderItemInfo, QuoteInfo, QuoteItemInfo, UserBalance} from 'src/app/models/models';
import {Address, Batch_Record, Currency, Item_Exception, Order, Order_Item, Order_Item_Batch, Order_Item_Exception, Price, Stock_Record, Store} from 'src/app/models/RestModels';
import {BaseComponent} from '../base/base.component';
import { environment } from 'src/environments/environment';
import { QUOTE_PRINT_CSS, QUOTE_PRINT_PDF_CSS } from './view-quote.styles';
import { Utils } from 'src/app/classes/Utils';

@Component({
	selector: 'app-view-quote',
	templateUrl: './view-quote.component.html',
	styles: [QUOTE_PRINT_CSS]
})
export class ViewQuoteComponent extends BaseComponent implements OnInit
{
	quote_info:QuoteInfo | null = null;
	@ViewChild('quotePrint') quotePrintRef!: ElementRef<HTMLElement>;
	total:number = 0;
	store_list: Store[];
	billing_address: Address | null = null;
	print80mm:boolean = false;

	show_resend_quote:boolean = false;
	resend_quote_name:string = '';
	resend_quote_email:string = '';
	billing_data = GetEmpty.billing_data();
	total_letra:string = '';
	currency:Currency = GetEmpty.currency();
	currency_list: Currency[];
	total_tax: number = 0;
	store:Store = GetEmpty.store();
	user_balance:UserBalance = null;

	subtotal:number = 0;

	// Batch selection
	show_batch_selection: boolean = false;
	batch_items_pending: {quote_item_info: QuoteItemInfo, batches: Batch_Record[], selections: {batch: Batch_Record, qty: number}[]}[] = [];
	current_batch_item_index: number = 0;
	send_to_credit_pending: boolean = false;

	// View batches
	show_view_batches: boolean = false;
	view_batches_loading: boolean = false;
	view_batches_list: Batch_Record[] = [];

	// Installments
	show_installments: boolean = false;
	installment_months: number = 0;
	installment_round_amount: number = 1;
	first_payment_date: string = '';
	frequency: 'WEEKLY' | 'MONTHLY' | '2X_MONTHLY' | 'NONE' = 'MONTHLY';

	// Delivery date required when creating an order from a quote
	show_delivery_schedule: boolean = false;
	delivery_schedule: string = '';
	deliver_immediately: boolean = false;
	increase_credit_limit: boolean = false;

	ngOnInit(): void
	{
		this.sink = this.route.paramMap.pipe
		(
			mergeMap((param_map)=>
			{
				return forkJoin
				({
					stores: this.rest.getStores(),
					currencies: this.rest.currency.search({limit:99999}),
					quote_info:this.rest.quote_info.get(param_map.get('id'))
				})
			}),
			mergeMap((responses)=>
			{
				this.currency_list = responses.currencies.data;
				this.store = responses.stores.data.find(s=>s.id == responses.quote_info.quote.store_id)
					?? responses.quote_info.store
					?? GetEmpty.store();

				let obs:Observable<Address | null> | null = null;

				if( !responses.quote_info?.client_user )
				{
					obs = of(null);
				}
				else
				{
					if( responses.quote_info.client_user.default_billing_address_id )
					{
						obs = this.rest.address.get( responses.quote_info.client_user.default_billing_address_id);
					}
					else
					{
						obs = this.rest.address.search
						({
							eq		:{ user_id:responses.quote_info.client_user.id },
							nn		:['rfc'],
							limit	:1
						})
						.pipe
						(
							mergeMap((response)=>
							{
								if( response.total > 0 )
								{
									return of( response.data[0] );
								}
								else
								{
									return of( null );
								}
							})
						);
					}
				}

				return forkJoin({
					quote_info:of(responses.quote_info),
					stores: of( responses.stores ),
					address: obs ?? of( null ),
					billing_data: this.store?.default_billing_data_id
						? this.rest.billing_data.get( this.store.default_billing_data_id )
						: of( null ),
					balance: this.searchUserBalance( responses.quote_info.quote.client_user_id ),
					creator: responses.quote_info.quote.created_by_user_id ? this.rest.user.get( responses.quote_info.quote.created_by_user_id ) : of(null)
				})
			})
		)
		.subscribe((response)=>
		{
			this.currency = this.currency_list.find(c=>c.id == response.quote_info.quote.currency_id) as Currency;
			this.billing_data = response.billing_data ?? GetEmpty.billing_data();
			this.billing_address = response.address;
			this.store_list = response.stores.data;
			this.quote_info = response.quote_info;
			this.quote_info.creator = response.creator;
			//El TOTAL es canonico (lo que paga el cliente); el IVA se deriva como
			//Total - Subtotal para que el desglose siempre cuadre, incluyendo
			//cotizaciones historicas guardadas con la formula anterior.
			let active_items = (response.quote_info.items || []).filter((i)=>i.quote_item.status == 'ACTIVE');
			this.subtotal = this.rest.roundTo2(active_items.reduce((p,i)=>p+i.quote_item.subtotal,0));
			this.total = this.rest.roundTo2(active_items.reduce((p,i)=>p+i.quote_item.total,0));
			this.total_tax = this.rest.roundTo2(this.total - this.subtotal);
			let n = new NumeroALetras('','','/100','/100');
			this.total_letra = n.numeroALetras( this.total );
			this.user_balance = response.balance;

		},(error)=>this.showError(error));
	}

	printDirect(evt:Event)
	{
		this.print( evt );
	}

	print(evt:Event)
	{
		this.rest.hideMenu();
		setTimeout(()=>{
			console.log( evt );
			window.print();
		},200);
	}

	designCustomQuote()
	{
		this.router.navigate(['/custom-quote-designer', this.quote_info.quote.id]);
	}

	getOrderItemFromQuoteItem(quote_item_info: QuoteItemInfo, item_group: number, sat_ieps: number): OrderItemInfo
	{
		let order_item = GetEmpty.order_item(quote_item_info.item);

		order_item.tax_included = quote_item_info.quote_item.tax_included;
		// Hay un error en la cotización los precios los pone mal
		// Como siempre los pone con iva lo ponemos hardcoded HERE
		order_item.discount = quote_item_info.quote_item.discount;
		order_item.discount_percent = quote_item_info.quote_item.discount_percent;
		order_item.subtotal = quote_item_info.quote_item.subtotal;
		order_item.total = quote_item_info.quote_item.total;
		order_item.qty = quote_item_info.quote_item.qty;
		order_item.tax = quote_item_info.quote_item.tax;
		order_item.status = quote_item_info.quote_item.status;
		order_item.unitary_price = quote_item_info.quote_item.unitary_price;
		order_item.unitary_price_meta = quote_item_info.quote_item.unitary_price_meta;
		order_item.original_unitary_price = quote_item_info.quote_item.original_unitary_price;
		order_item.note = quote_item_info.quote_item.note;

		// Preserve item options grouping from quote
		order_item.item_option_id = quote_item_info.quote_item.item_option_id || null;
		order_item.item_option_qty = quote_item_info.quote_item.item_option_qty || 0;
		order_item.item_group = quote_item_info.quote_item.item_group || item_group;

		return {
			order_item,
			item: quote_item_info.item,
			category: quote_item_info.category,
			prices: [] as Price[],
			records: [] as Stock_Record[],
			options: [] as ItemOptionInfo[],
			exceptions: [] as Item_Exception[],
			order_item_exceptions: [] as Order_Item_Exception[],
			serials: [],
			serials_string: '',
			batches: [] as Order_Item_Batch[],
			created: new Date(),
			commanda_type_id: quote_item_info.item.commanda_type_id,
		};
	}


	searchUserBalance(user_id:number):Observable<UserBalance|null>
	{
		if( this.rest.is_offline )
			return of( null );

		return this.rest.user.search({csv:{ id:[ user_id ]},limit:1}).pipe
		(
			mergeMap((response)=>{

				if( response.data.length )
					return of( response.data[0] );

				return of( null );
			})
		);
	}

	startCreateOrderProcess()
	{
		this.delivery_schedule = '';
		this.deliver_immediately = false;
		this.increase_credit_limit = false;
		this.show_delivery_schedule = true;
	}

	onDeliverImmediatelyChange(deliver_immediately: boolean)
	{
		this.deliver_immediately = deliver_immediately;

		if( deliver_immediately )
		{
			this.delivery_schedule = '';
			// La entrega inmediata manda la orden al punto de venta, no es venta a credito
			this.increase_credit_limit = false;
		}
	}

	cancelDeliverySchedule()
	{
		this.show_delivery_schedule = false;
	}

	confirmDeliverySchedule()
	{
		if( !this.deliver_immediately && !this.delivery_schedule )
		{
			this.showError('La fecha de entrega es requerida');
			return;
		}

		this.show_delivery_schedule = false;
		this.continueCreateOrderProcess();
	}

	continueCreateOrderProcess()
	{
		// Entrega inmediata: la orden se manda al punto de venta para cobrarse ahi mismo,
		// asi que no aplica ninguna validacion de credito aunque la cotizacion tenga cliente.
		if( this.deliver_immediately )
		{
			this.proceedWithBatchCheck(false);
			return;
		}

		if( this.quote_info?.quote?.client_user_id )
		{
			if( !this.rest.current_permission.add_credit_sales )
			{
				this.showError('No cuenta con permiso para realizar ventas a crédito');
				return;
			}

			if( !this.quote_info?.client_user || !this.user_balance )
			{
				this.showError('La cotización debe tener un cliente para realizar una venta a crédito');
				return;
			}

			let balance_futuro = Math.abs( this.user_balance.balance - this.total );

			if( this.quote_info.client_user.credit_limit >= balance_futuro )
			{
				this.showInstallmentsModal();
			}
			else
			{
				if( this.increase_credit_limit && this.rest.current_permission.add_credit_limit )
				{
					this.showInstallmentsModal();
				}
				else
				{
					this.showError('Crédito insuficiente. Active la opción para incrementar el límite de crédito.');
				}
			}
			return;
		}

		this.proceedWithBatchCheck(false);
	}

	showInstallmentsModal()
	{
		this.first_payment_date = new Date().toISOString().split('T')[0];
		this.installment_months = 0;
		this.show_installments = true;
	}

	cancelInstallments()
	{
		this.show_installments = false;
	}

	confirmInstallments()
	{
		if (this.installment_months > 0)
		{
			if (!this.first_payment_date || !this.frequency)
			{
				this.showError('La fecha de primer pago y frecuencia son requeridas si hay mensualidades');
				return;
			}

			if (!this.installment_round_amount || this.installment_round_amount < 1)
			{
				this.showError('El redondeo de pagos debe ser un número entero mayor o igual a 1 (usa 1 para no redondear)');
				return;
			}
		}
		this.show_installments = false;
		this.proceedWithBatchCheck(true);
	}

	proceedWithBatchCheck(send_to_credit: boolean)
	{
		let batch_items = this.quote_info.items.filter(quote_item_info =>
			quote_item_info.item.batch_option !== 'NONE' && quote_item_info.quote_item.status == 'ACTIVE'
		);

		if (batch_items.length === 0)
		{
			this.createOrder(send_to_credit);
			return;
		}

		this.send_to_credit_pending = send_to_credit;
		this.batch_items_pending = batch_items.map(quote_item_info => ({
			quote_item_info,
			batches: [] as Batch_Record[],
			selections: [] as {batch: Batch_Record, qty: number}[]
		}));
		this.current_batch_item_index = 0;
		this.loadBatchesForCurrentItem();
	}

	loadBatchesForCurrentItem()
	{
		let pending = this.batch_items_pending[this.current_batch_item_index];
		this.is_loading = true;

		this.subs.sink = this.rest.batch_record.search({
			eq: {
				item_id: pending.quote_item_info.item.id,
				store_id: this.quote_info.quote.store_id,
				is_current: 1
			},
			gt: { qty: 0 },
			limit: 1000
		}).subscribe(
			(response) => {
				pending.batches = response.data;
				pending.selections = response.data.map(b => ({ batch: b, qty: 0 }));
				this.is_loading = false;
				this.show_batch_selection = true;
			},
			(error) => {
				this.is_loading = false;
				this.showError(error);
			}
		);
	}

	getCurrentBatchPending()
	{
		return this.batch_items_pending[this.current_batch_item_index];
	}

	onBatchConfirm()
	{
		let pending = this.batch_items_pending[this.current_batch_item_index];
		let total_selected = pending.selections.reduce((sum, s) => sum + s.qty, 0);
		let required_qty = pending.quote_item_info.quote_item.qty;

		if (total_selected !== required_qty)
		{
			this.showError(`La cantidad seleccionada (${total_selected}) debe ser igual a la cantidad requerida (${required_qty})`);
			return;
		}

		// Move to next batch item or finish
		this.current_batch_item_index++;

		if (this.current_batch_item_index < this.batch_items_pending.length)
		{
			this.show_batch_selection = false;
			setTimeout(() => this.loadBatchesForCurrentItem(), 200);
		}
		else
		{
			this.show_batch_selection = false;
			this.createOrder(this.send_to_credit_pending);
		}
	}

	onBatchCancel()
	{
		this.show_batch_selection = false;
		this.batch_items_pending = [];
		this.current_batch_item_index = 0;
	}

	viewBatches(quote_item_info: QuoteItemInfo)
	{
		this.show_view_batches = true;
		this.view_batches_loading = true;
		this.view_batches_list = [];

		this.subs.sink = this.rest.batch_record.search({
			eq: {
				item_id: quote_item_info.item.id,
				store_id: this.quote_info.quote.store_id,
				is_current: 1
			},
			gt: { qty: 0 },
			limit: 1000
		}).subscribe(
			(response) => {
				this.view_batches_list = response.data;
				this.view_batches_loading = false;
			},
			(error) => {
				this.view_batches_loading = false;
				this.showError('Error al cargar lotes');
			}
		);
	}

	createOrder(send_to_credit: boolean) {
		this.is_loading = true;
		let version_created = this.rest.getVersion();

		let order: Order = {
			quote_id: this.quote_info.quote.id as number,
			address: '',
			amount_paid: 0,
			ares: 0,
			authorized_by: null,
			billing_address_id: null,
			billing_data_id: null,
			cancellation_reason: '',
			cancellation_timestamp: null,
			cancelled_by_user_id: null,
			cashier_user_id: this.rest.current_user.id,
			city: '',
			client_name: this.quote_info.quote.name,
			client_user_id: this.quote_info.quote.client_user_id,
			closed_timestamp: null,
			created: new Date(),
			currency_id: this.quote_info.quote.currency_id,
			delivery_schedule: this.deliver_immediately
				? null
				: Utils.getLocalMysqlStringFromDate(new Date(this.delivery_schedule)),
			delivery_status: 'PENDING',
			delivery_user_id: null,
			discount: 0,
			discount_calculated: 0,
			external_id: null,
			facturacion_code: '',
			//facturacion_timestamp: null,
			guests: 1,
			installment_months: send_to_credit ? this.installment_months : 0,
			installment_round_amount: send_to_credit ? this.installment_round_amount : 0,
			first_payment_date: (send_to_credit && this.installment_months > 0) ? this.first_payment_date : null,
			frequency: (send_to_credit && this.installment_months > 0) ? this.frequency : 'NONE',
			lat: null,
			lng: null,
			marked_for_billing: 'NO',
			paid_status: 'PENDING',
			period_id: null,
			price_type_id: this.quote_info?.client_user?.price_type_id || 1,
			sat_domicilio_fiscal_receptor: '',
			sat_exchange_rate: 22,
			sat_factura_id: null,
			sat_ieps: 0,
			sat_isr: 0,
			sat_regimen_capital_receptor: '',
			sat_regimen_fiscal_receptor: '',
			service_type: 'QUICK_SALE',
			status: send_to_credit ? 'CLOSED' : 'PENDING',
			store_id: this.quote_info.quote.store_id,
			sync_id: this.rest.getSyncId(),
			system_activated: null,
			table_id: null,
			tag: '',
			tax: 0,
			tax_percent: this.quote_info.quote.tax_percent,
			total: this.total,
			updated: new Date(),
			version_created,
			version_updated: version_created
		};

		let item_group: number = Date.now();

		console.log('Creando ', order.currency_id);

		let order_info: OrderInfo = {
			order: order,
			client: null,
			purchase: null,
			cashier: this.rest.current_user,
			sat_factura: null,
			delivery_user: null,
			items: this.buildOrderItems(item_group, order.sat_ieps),
			structured_items: [],
			store: GetEmpty.store(),
			offers: []
		};

		let update_credit_limit: Observable<any> = of(null);
		if( send_to_credit && this.increase_credit_limit && this.quote_info.client_user && this.user_balance )
		{
			let required_credit_limit = Math.abs(this.user_balance.balance - this.total);
			if( required_credit_limit > this.quote_info.client_user.credit_limit )
			{
				update_credit_limit = this.rest.user.update({
					id: this.quote_info.client_user.id,
					credit_limit: required_credit_limit
				});
			}
		}

		this.subs.sink = update_credit_limit
		.pipe
		(
			mergeMap(() => this.rest.order_info.create(order_info)),
			mergeMap((response) =>
			{
				return forkJoin
				({
					order_info: of(response),
					update: this.rest.setOrderActive(response.order.id)
				});
			}),
			mergeMap((response) => {
				this.is_loading = false;
				this.showSuccess('La orden se ha creado correctamente');

				let payload = { order_id: response.order_info.order.id };
				let closed_log = (r): Observable<any> => {
					console.log('Orden Cerrada');
					return of(null);
				};

				let obs = !send_to_credit
					? of(null)
					: this.rest.update('closeOrder', payload).pipe(
						mergeMap(closed_log)
					);

				return forkJoin({
					order_info: of(response.order_info),
					closed: obs
				});
			})
		)
		.subscribe
		(
			(response) =>
			{
				this.router.navigate(send_to_credit
					? ['/view-order', response.order_info.order.id]
					: ['/pos', response.order_info.order.id]
				);
			},
			(error) => this.showError(error)
		);
	}

	buildOrderItems(item_group: number, sat_ieps: number): OrderItemInfo[]
	{
		let result: OrderItemInfo[] = [];
		let index = 0;

		for (let quote_item_info of this.quote_info.items)
		{
			if (quote_item_info.quote_item.status !== 'ACTIVE') continue;

			let batch_pending = this.batch_items_pending.find(bp => bp.quote_item_info === quote_item_info);

			let oii = this.getOrderItemFromQuoteItem(quote_item_info, item_group + index, sat_ieps);

			if (batch_pending)
			{
				let selected = batch_pending.selections.filter(s => s.qty > 0);
				let total_qty = selected.reduce((a, s) => a + s.qty, 0);
				oii.order_item.qty = total_qty;

				oii.batches = selected.map(sel => ({
					id: 0,
					order_item_id: 0,
					batch: sel.batch.batch,
					expiration_date: sel.batch.expiration_date,
					qty: sel.qty,
					created: new Date(),
					updated: new Date()
				}));

				//Recalcular con la misma formula del guardado (IVA derivado,
				//subtotal+tax==total) en vez de multiplicaciones sin redondeo.
				let item = quote_item_info.item;
				let effective_tax_percent = item.applicable_tax == 'EXEMPT'
					? 0
					: item.applicable_tax == 'PERCENT'
						? item.tax_percent
						: this.quote_info.quote.tax_percent;
				this.rest.updateOrderItemPrice(oii.order_item, effective_tax_percent, 0);
			}

			result.push(oii);
			index++;
		}

		return result;
	}

	showSendQuote()
	{
		this.resend_quote_name = this.quote_info.quote.name;
		this.resend_quote_email = this.quote_info.quote.email;
		this.show_resend_quote = true;
	}

	sendQuote(evt:Event)
	{
		this.is_loading = true;

		// Reutilizamos el MISMO HTML que se muestra en pantalla (.quote-print) para que el
		// PDF del correo sea identico a la vista y respete las preferencias del sistema
		// (p.ej. quote_agent_footer_enabled ya controla el bloque del agente via *ngIf).
		let node:HTMLElement | null = null;

		if( this.quotePrintRef && this.quotePrintRef.nativeElement )
		{
			node = this.quotePrintRef.nativeElement.cloneNode(true) as HTMLElement;
			// Quitamos botones/acciones que no van en el documento.
			node.querySelectorAll('.d-print-none').forEach((el) => el.remove());
		}

		let body_html = node ? node.outerHTML : '';

		// QUOTE_PRINT_PDF_CSS = el CSS de la cotizacion SIN las container-queries de 80mm,
		// para forzar el layout de escritorio en el PDF del correo.
		let pdf_css = QUOTE_PRINT_PDF_CSS;

		let html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>${pdf_css}</style>
<style>
/* PDF del correo: el renderizador del pdfservice NO soporta flexbox ni display:table-cell
   (el encabezado y los totales usan flex en pantalla). Usamos FLOATS para el encabezado de
   3 columnas y margin-auto para los totales, que TODOS los renderizadores de PDF manejan.
   Tambien desactivamos la container-query de 80mm y fijamos un ancho de pagina. */
body { margin: 0; }
.quote-print { container-type: normal !important; width: 760px !important; max-width: 760px !important; margin: 0 auto; }

/* Encabezado en 3 columnas con floats: logo (izq) | empresa (centro) | folio/fechas (der) */
.quote-header { display: block !important; overflow: hidden; border-bottom: 1px solid #ccc; }
.quote-header-logo { float: left !important; width: 90px !important; }
.quote-header-logo img { width: 90px !important; height: auto; }
.quote-header-folio { float: right !important; width: 240px !important; text-align: right !important; }
.quote-header-info { margin: 0 250px 0 100px !important; text-align: left !important; }
.branch-info { display: inline-table; }

/* Totales alineados a la derecha sin depender de flex */
.quote-totals-wrap { display: block !important; }
.quote-totals-table { margin-left: auto !important; margin-right: 0 !important; }
.amount-letters { margin-left: auto !important; text-align: right !important; }

/* Fondos grises: el renderizador no resuelve var(--section-bg), asi que ponemos el gris
   literal y forzamos la impresion de fondos. */
.quote-print, .quote-print * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
.quote-section-label,
.quote-totals-table th,
.quote-agent-table thead th { background: #e6e6e6 !important; }
</style>
</head>
<body>${body_html}</body>
</html>`;

		let html_object =
		{
			html: html,
			orientation: 'P',
			default_font_size: 9,
			default_font: 'Helvetica',
			download_name: ''
		}

		let url = environment.app_settings.pdf_service_url + "/index.php";

		this.sink = this.rest.externalPost(url, html_object)
		.pipe
		(
			mergeMap((response) =>
			{
				const pdf_file = new File([response], 'cotizacion.pdf', { type: 'application/pdf' });

				const formData = new FormData();
				formData.append('pdf', pdf_file);
				formData.append('quote_id', this.quote_info.quote.id.toString());
				formData.append('email', this.resend_quote_email);
				formData.append('name', this.resend_quote_name || '');

				return this.rest.send_quote.create(formData);
			})
		)
		.subscribe
		({
			next: (response) =>
			{
				this.showSuccess('El correo se envió correctamente');
				this.is_loading = false;
				(evt.target as HTMLFormElement).reset();
			},
			error: (error) =>
			{
				this.showError('Error al enviar el correo');
				this.is_loading = false;
			}
		});
	}
}
